import React, { useState, useRef, useEffect } from "react";
import { CloudUpload, FileText, CheckCircle, ChevronDown } from "lucide-react";
import { ReactComponent as Eye } from "../../../../icons/View_eye.svg";
import { ReactComponent as Delete } from "../../../../icons/View_dlt.svg";
import { GridcoTitle } from "../../components";
import { TabSwitcher } from "../../Gridco_Das";
import Hamburger from "../../../../components/Hamburger";
import { ErrorPopup } from "../../../../components/ErrorHandling";
import { uploadGridcoDcFile, uploadGridcoScheduleFile, getGridcoModReportExcel } from "../../../../Rest_api/RestApi";

type UploadedFile = {
    type: string;
    file: File;
    id: number;
};

type FileRowProps = {
    fileName: string;
    uploadedFile: File | null;
    fileError: string | null;
    onFileChange: (fileName: string, file: File | null) => void;
    onFileError: (fileName: string, error: string | null) => void;
};

const FileRow: React.FC<FileRowProps> = ({
    fileName,
    uploadedFile,
    fileError,
    onFileChange,
    onFileError,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const fileExtension = file.name.split(".").pop()?.toLowerCase();
            if (
                fileExtension !== "xlsx" &&
                fileExtension !== "csv" &&
                fileExtension !== "pdf"
            ) {
                onFileError(
                    fileName,
                    "Invalid file type. Only .xlsx, .csv and .pdf files are allowed."
                );
                onFileChange(fileName, null);
                return;
            }
            onFileError(fileName, null);
            onFileChange(fileName, file);
        } else {
            onFileChange(fileName, null);
            onFileError(fileName, null);
        }
    };

    const handleViewFile = () => {
        if (uploadedFile) {
            const url = URL.createObjectURL(uploadedFile);
            window.open(url, "_blank");
        }
    };

    const handleDeleteFile = () => {
        onFileChange(fileName, null);
        onFileError(fileName, null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const isReady = uploadedFile && !fileError;
    const isError = !uploadedFile && fileError;

    return (
        <div className="flex items-center justify-between py-2 px-1 border mb-3 border-[#F3F5F8] last:border-b-0">
            <div className="flex items-center gap-3">
                <FileText size={20} style={{ color: "#8C8A96" }} />
                <span className="text-sm font-medium text-[#525062]">{fileName}</span>
            </div>

            {isReady ? (
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleViewFile}
                            className="text-[#F1935C] hover:opacity-80 transition-opacity p-1 rounded"
                        >
                            <Eye />
                        </button>
                        <button
                            className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
                            style={{ backgroundColor: "#00BF6F", color: "#fff" }}
                        >
                            <CheckCircle size={14} /> <span>Ready</span>
                        </button>
                        <button
                            onClick={handleDeleteFile}
                            className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
                        >
                            <Delete className="w-8 h-8" />
                        </button>
                    </div>
                </div>
            ) : isError ? (
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <button
                            className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
                            style={{ backgroundColor: "#FDE6E8", color: "#B32130" }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="10" />
                                <path d="m15 9-6 6" />
                                <path d="m9 9 6 6" />
                            </svg>{" "}
                            <span>Error</span>
                        </button>
                        <button
                            onClick={handleDeleteFile}
                            className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
                        >
                            <Delete className="w-8 h-8" />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleUploadClick}
                        className="flex items-center justify-center gap-2 w-[90px] h-[30px] text-xs rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors"
                        style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
                    >
                        <CloudUpload size={14} /> Upload
                    </button>
                </div>
            )}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept=".xlsx,.csv,.pdf"
            />
        </div>
    );
};

const FileRegexPatterns: { [key: string]: RegExp } = {
    AVT: /^AVT \d{2}\.\d{2}\.\d{4}\s*\.xlsx(?: - .*\.csv)?$/i,
    "Day Ahead Schedule for MTOA": /^Day Ahead Schedule for MTOA.*\(.*\).*\.xlsx$/i,
    MAADURGA_DIRECT: /^Maa\s*Durga.*\.xlsx$/i,
    "Declared Capacity of GKEL": /^Declared Capacity of GKEL for\s+\d{2}\.\d{2}\.\d{4}\s*\.xlsx$/i,
    "IEX DOR GRIDCO": /^IEX\d{6}DOR_GDC0001_OR0_GRIDCO\.xls$/i,
    "JSW Energy Utkal Schedule": /^JSW Energy Utkal Schedule & GRIDCO allocation for \d{2}\.\d{2}\.\d{2}\.xlsx$/i,
    "NetSchdReportBreakup ISGS": /^NetSchdReportBreakup@ODISHA_STATE@ISGS@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/i,
    "NetSchdReportBreakup OA_GNA": /^NetSchdReportBreakup@ODISHA_STATE@OA_GNA@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/i,
    "NetSchdReportBreakup OA_REMC": /^NetSchdReportBreakup@ODISHA_STATE@OA_REMC@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/i,
    "OPGC STAGE-2 DC": /^OPGC STAGE-2 DC\s+\d{2}\.\d{2}\.\d{4}\s+-\s+R\d+\.xlsx$/i,
    "RTM_IEX DOR GRIDCO": /^RTM_IEX\d{6}DOR_GDC0001_OR0_GRIDCO(\s?\(1\))?\.XLS$/i,
    "SLDC LDS": /^SLDC LDS \d{2} (Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?) \d{4} \(Rev-\d+\)\.xlsx$/i,
    "NBVL(IPP)": /^NBVLIPP_DAY_AHEAD_SCHEDULE_TO_GRIDCO_FOR_(\d{2}\.\d{2}\.\d{4})\.xlsx$/i,
    MAADURGA: /^DC_INJECTION_\d{2}\.\d{2}\.\d{4}\.xlsx$/i,
    jipl_dc_pdf: /^JIPL DC for DD \d{2}\.\d{2}\.\d{4}\.pdf$/i,
    sldc_report: /^SLDC_Report__(\d{2})(\d{2})(\d{4})\.xlsx(?: - .*\.csv)?$/i,
    "VC Generator": /^VC_all_generators\.xlsx$/i,
    Intra_State_RE: /^Intra_State_RE\.xlsx$/i,
    CGP_Injection: /^CGP_Injection\.xlsx$/i,
    Buffer: /^Buffer\.xlsx$/i,
    Banking: /^Banking.*\.xlsx$/i,
    "Short-Term": /^Short[ -]Term.*\.xlsx$/i,
    Demand: /^Actual Demand DD (\d{2})\.(\d{2})\.(\d{4}).*\.xlsx$/i,
    IEX_XLSX: /^IEX(\d{6})DOR_GDC0001_OR0_GRIDCO.*\.xlsx$/i,
};

const RegexToFileTypeMap: { [key: string]: string } = {
    AVT: "OPGC-1 (IBTPS Unit -1 & 2)",
    "OPGC STAGE-2 DC": "OPGC-2 (IBTPS Unit -3 & 4)",
    "SLDC LDS": "Vedanta",
    "Declared Capacity of GKEL": "GMR",
    "JSW Energy Utkal Schedule": "JSWEUL",
    "NBVL(IPP)": "NBVL",
    "NetSchdReportBreakup ISGS": "Net Schedule ISGS",
    "NetSchdReportBreakup OA_GNA": "Net Schedule OA GNA",
    "NetSchdReportBreakup OA_REMC": "Net Schedule OA REMC",
    "RTM_IEX DOR GRIDCO": "RTM",
    "IEX DOR GRIDCO": "IEX",
    "Day Ahead Schedule for MTOA": "Maa Durga",
    MAADURGA_DIRECT: "Maa Durga",
    MAADURGA: "Maa Durga",
    jipl_dc_pdf: "JIPL",
    sldc_report: "State Hydro Generation",
    "VC Generator": "VC for Generators",
    Intra_State_RE: "Intra State RE",
    CGP_Injection: "CGP Injection",
    Buffer: "Buffer",
    Banking: "Banking",
    "Short-Term": "Short Term Buy/Sell",
    Demand: "Demand",
    IEX_XLSX: "IEX",
};

const getValidPatternsForFileType = (fileName: string): RegExp[] => {
    const patterns: RegExp[] = [];

    Object.entries(RegexToFileTypeMap).forEach(([regexKey, mappedType]) => {
        if (mappedType === fileName) {
            const pattern = FileRegexPatterns[regexKey];
            if (pattern) {
                patterns.push(pattern);
            }
        }
    });

    return patterns;
};

const detectFileType = (fileName: string, currentTabTypes: string[]): string => {
    for (const [regexKey, pattern] of Object.entries(FileRegexPatterns)) {
        if (pattern.test(fileName)) {
            const mappedType = RegexToFileTypeMap[regexKey];
            if (mappedType && currentTabTypes.includes(mappedType)) return mappedType;

            // Special handling for templates in DC tab (mapping to base types)
            if (regexKey.includes("_TEMPLATE")) {
                const baseType = RegexToFileTypeMap[regexKey.replace("_TEMPLATE", "")];
                if (baseType && currentTabTypes.includes(baseType)) return baseType;
            }
        }
    }
    return "";
};

// ── File types required for DAM Performance Analysis ──
const FILE_TYPES = [
    "VC for Generators (Optional)",
    "Net Schedule ISGS",
    "State Hydro and OPGC-1 (IBTPS Unit -1 & 2)",
    "OPGC-2 (IBTPS Unit -3 & 4)",
    "Vedanta",
    "GMR",
    "Net Schedule OA REMC",
    "Net Schedule OA GNA",
    "OA Gridco",
    "DAM",
    "RTM",
    "TAM",
];

// ── Backend alias map for Schedule file types ──
const Schedule_FileTypeBackendAlias: { [key: string]: string } = {
    "VC for Generators (Optional)": "vc-generator",
    "Net Schedule ISGS": "isgs",
    "State Hydro and OPGC-1 (IBTPS Unit -1 & 2)": "opgc-1",
    "OPGC-2 (IBTPS Unit -3 & 4)": "opgc-2",
    "Vedanta": "vedanta",
    "GMR": "gmr",
    "Net Schedule OA REMC": "oa-remc",
    "Net Schedule OA GNA": "oa-gna",
    "OA Gridco": "oa-gridco",
    "DAM": "dam",
    "RTM": "rtm",
    "TAM": "tam",
};

// ── File types required for DC (Declared Capacity) ──
const DC_FILE_TYPES = [
    "VC for Generators",
    "Net Schedule ISGS",
    "OPGC-1 (IBTPS Unit -1 & 2)",
    "OPGC-2 (IBTPS Unit -3 & 4)",
    "Vedanta",
    "GMR",
    "JIPL",
    "JSWEUL",
    "NBVL",
    "Maa Durga",
    "Net Schedule OA REMC",
    "Net Schedule OA GNA",
    "State Hydro Generation",
    "Intra State RE",
    "CGP Injection",
    "Buffer",
    "Banking",
    "Short Term Buy/Sell",
    "Demand",
    "IEX",
];

// ── Backend alias map for DC file types ──
const DC_FileTypeBackendAlias: { [key: string]: string } = {
    "VC for Generators": "vc-generator",
    "Net Schedule ISGS": "isgs",
    "OPGC-1 (IBTPS Unit -1 & 2)": "opgc-1",
    "OPGC-2 (IBTPS Unit -3 & 4)": "opgc-2",
    "Vedanta": "vedanta",
    "GMR": "gmr",
    "JIPL": "jipl",
    "JSWEUL": "jsweul",
    "NBVL": "nbvl",
    "Maa Durga": "maa-durga",
    "Net Schedule OA REMC": "oa-remc",
    "Net Schedule OA GNA": "oa-gna",
    "State Hydro Generation": "state-hydro",
    "Intra State RE": "state-re",
    "CGP Injection": "cgp",
    "Buffer": "buffer",
    "Banking": "banking",
    "Short Term Buy/Sell": "short-term-buy-sell",
    "Demand": "demand",
    "IEX": "dam",
};

// ── Upload mode tabs ──
const UPLOAD_TABS = ["DC"] as const; // Only DC for now, Schedule commented out
// const UPLOAD_TABS = ["DC", "Schedule"] as const;
type UploadTab = (typeof UPLOAD_TABS)[number] | "Schedule";

// ── Simple header title ──
function UploadTitle() {
    return (
        <div className="flex items-center gap-2 p-1 px-4 bg-[#F3F5F8] rounded-xl">
            <span className="text-sm font-semibold text-[#34656D]">Upload Data (DC)</span>
        </div>
    );
}

// ── Tab switcher component (Disabled for now) ──
function UploadTabSwitcher({
    activeTab,
    dcCount,
    dcTotal,
}: {
    activeTab: string;
    dcCount: number;
    dcTotal: number;
}) {
    return (
        <div className="flex items-center gap-1 p-1 rounded-xl bg-[#F3F5F8]">
            <button
                className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-lg bg-white text-[#34656D] shadow-sm"
            >
                {activeTab}
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#34656D] text-white">
                    {dcCount}/{dcTotal}
                </span>
            </button>
        </div>
    );
}

// ── Custom Select Component for Height Control ──
function CustomSelect({
    options,
    value,
    onChange,
    placeholder = "Select File",
}: {
    options: string[];
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative inline-block w-[300px]" ref={containerRef}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between border border-[#C5C5CBD9] rounded-lg p-2 bg-white cursor-pointer text-sm text-[#8C8A96]"
            >
                <span className="truncate">{value || placeholder}</span>
                <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
            </div>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#C5C5CBD9] rounded-lg shadow-xl z-[100] overflow-hidden">
                    <div className="max-height-[250px] overflow-y-auto" style={{ maxHeight: '250px' }}>
                        <div
                            key="all-files"
                            className={`px-4 py-2 text-sm cursor-pointer hover:bg-[#F3F5F8] ${value === "All Files" ? "bg-[#EFFCFF] text-[#34656D] font-medium" : "text-[#8C8A96]"}`}
                            onClick={() => {
                                onChange("All Files");
                                setIsOpen(false);
                            }}
                        >
                            All Files
                        </div>
                        {options.map((option, idx) => (
                            <div
                                key={idx}
                                className={`px-4 py-2 text-sm cursor-pointer hover:bg-[#F3F5F8] ${value === option ? "bg-[#EFFCFF] text-[#34656D] font-medium" : "text-[#8C8A96]"}`}
                                onClick={() => {
                                    onChange(option);
                                    setIsOpen(false);
                                }}
                            >
                                {option}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function DamPerformanceUpload() {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const activeUploadTab = "DC";
    const [selectedDate, setSelectedDate] = useState<string>("");

    const [dcSubmitted, setDcSubmitted] = useState(false);

    // ── Schedule state ──
    const [scheduleFiles, setScheduleFiles] = useState<UploadedFile[]>([]);
    const [scheduleFileErrors, setScheduleFileErrors] = useState<Record<string, string | null>>({});
    const [scheduleSelectedFileType, setScheduleSelectedFileType] = useState<string>("All Files");

    // ── DC state ──
    const [dcFiles, setDcFiles] = useState<UploadedFile[]>([]);
    const [dcFileErrors, setDcFileErrors] = useState<Record<string, string | null>>({});
    const [dcSelectedFileType, setDcSelectedFileType] = useState<string>("All Files");

    const scheduleBulkRef = useRef<HTMLInputElement>(null);
    const dcBulkRef = useRef<HTMLInputElement>(null);

    // ── Helpers to get/set state based on active tab ──
    // ── Helpers to get/set state based on active tab ──
    const isSchedule = false; // Always false as only DC tab is active
    const currentFileTypes = DC_FILE_TYPES; // Always DC_FILE_TYPES
    const currentFiles = dcFiles; // Always dcFiles
    const setCurrentFiles = setDcFiles; // Always setDcFiles
    const currentFileErrors = dcFileErrors; // Always dcFileErrors
    const setCurrentFileErrors = setDcFileErrors; // Always setDcFileErrors
    const currentSelectedFileType = dcSelectedFileType; // Always dcSelectedFileType
    const setCurrentSelectedFileType = setDcSelectedFileType; // Always setDcSelectedFileType
    const currentBulkRef = dcBulkRef; // Always dcBulkRef

    const handleBulkUploadClick = () => {
        currentBulkRef.current?.click();
    };

    const handleBulkUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!selectedDate) {
            setError("Please select a date first");
            event.target.value = "";
            return;
        }

        if (!files || files.length === 0) {
            setError("Please select files");
            return;
        }

        const validExtensions = [".xlsx", ".xls", ".csv", ".pdf"];
        const newDetectedFiles: UploadedFile[] = [];
        const undetectedFiles: string[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const fileExtension = file.name
                .substring(file.name.lastIndexOf("."))
                .toLowerCase();

            if (!validExtensions.includes(fileExtension)) {
                setError(
                    `Invalid file format for ${file.name}. Please select only XLSX, XLS, CSV or PDF files.`
                );
                event.target.value = "";
                return;
            }

            const detectedType = detectFileType(file.name, currentFileTypes);

            if (!detectedType) {
                undetectedFiles.push(file.name);
                continue;
            }

            newDetectedFiles.push({
                type: detectedType,
                file: file,
                id: Date.now() + i,
            });
        }

        if (undetectedFiles.length > 0) {
            setError(
                `Could not auto-detect or match file type for the current tab:\n${undetectedFiles.join(
                    "\n"
                )}\n\nPlease upload these files individually or check if you are on the correct tab.`
            );
        }

        if (newDetectedFiles.length > 0) {
            setCurrentFiles((prev: UploadedFile[]) => {
                const affectedTypes = new Set(newDetectedFiles.map((f: UploadedFile) => f.type));
                const kept = prev.filter((f: UploadedFile) => !affectedTypes.has(f.type));
                return [...kept, ...newDetectedFiles];
            });
            setCurrentFileErrors((prev: Record<string, string | null>) => {
                const updated = { ...prev };
                newDetectedFiles.forEach((f: UploadedFile) => {
                    updated[f.type] = null;
                });
                return updated;
            });
        }

        event.target.value = "";
    };

    const handleFileChange = (fileName: string, file: File | null) => {
        if (!file) {
            setCurrentFiles((prev: UploadedFile[]) => prev.filter((f: UploadedFile) => f.type !== fileName));
            setCurrentFileErrors((prev: Record<string, string | null>) => ({ ...prev, [fileName]: null }));
            return;
        }

        const validPatterns = getValidPatternsForFileType(fileName);
        if (validPatterns.length > 0) {
            const isValid = validPatterns.some((pattern) => pattern.test(file.name));
            if (!isValid) {
                const errorMsg = `File name does not match required pattern for ${fileName}.`;
                handleFileError(fileName, errorMsg);
                setCurrentFiles((prev: UploadedFile[]) => prev.filter((f: UploadedFile) => f.type !== fileName));
                return;
            }
        }

        setCurrentFiles((prev: UploadedFile[]) => {
            const filtered = prev.filter((f: UploadedFile) => f.type !== fileName);
            return [
                ...filtered,
                {
                    type: fileName,
                    file,
                    id: Date.now(),
                },
            ];
        });
        setCurrentFileErrors((prev: Record<string, string | null>) => ({ ...prev, [fileName]: null }));
    };

    const handleFileError = (fileName: string, error: string | null) => {
        setCurrentFileErrors((prev: Record<string, string | null>) => ({ ...prev, [fileName]: error }));
    };

    const filesToDisplay =
        currentSelectedFileType === "All Files"
            ? currentFileTypes
            : currentFileTypes.filter((fileType: string) => fileType === currentSelectedFileType);

    const scheduleUploadedCount = FILE_TYPES.filter((type: string) =>
        scheduleFiles.find((f: UploadedFile) => f.type === type)
    ).length;

    const dcUploadedCount = DC_FILE_TYPES.filter((type) =>
        dcFiles.find((f) => f.type === type)
    ).length;

    const allDcFilesUploaded = dcUploadedCount === DC_FILE_TYPES.length;
    const allScheduleFilesUploaded = scheduleUploadedCount === FILE_TYPES.length;

    const isDateSelected = selectedDate !== "";

    // ── Generate Report: uploads DC files ──
    const handleGenerateReportDC = async () => {
        if (!isDateSelected) {
            setError("Please select a date first.");
            return;
        }
        try {
            setLoading(true);
            const formData = new FormData();

            const metaData = dcFiles.map((file, idx) => {
                const field = `file_${idx}`;
                formData.append(field, file.file);
                return {
                    field,
                    date: selectedDate,
                    type: DC_FileTypeBackendAlias[file.type] || file.type,
                };
            });

            formData.append("metaData", JSON.stringify(metaData));
            formData.append("date", selectedDate);

            await uploadGridcoDcFile(formData);
            setDcSubmitted(true);
            setError(null);

            // Download the excel report
            const blob = await getGridcoModReportExcel(selectedDate);
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = `mod-report-${selectedDate}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error("Error uploading DC files:", err);
            setError("Error uploading DC files. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ── Generate Report: uploads Schedule files ──
    const handleGenerateReport = async () => {
        if (!isDateSelected) {
            setError("Please select a date first.");
            return;
        }
        try {
            setLoading(true);
            const formData = new FormData();

            const metaData = scheduleFiles.map((file, idx) => {
                const field = `file_${idx}`;
                formData.append(field, file.file);
                return {
                    field,
                    date: selectedDate,
                    type: Schedule_FileTypeBackendAlias[file.type] || file.type,
                };
            });

            formData.append("metaData", JSON.stringify(metaData));
            formData.append("date", selectedDate);

            await uploadGridcoScheduleFile(formData);
            // TODO: Show report from GET API (to be added later)
            alert("Report generated successfully!");
        } catch (err) {
            console.error("Error uploading schedule files:", err);
            setError("Error uploading schedule files. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ── Simple tab switching (no API call) ──
    const handleTabChange = (tab: UploadTab) => {
        // setActiveUploadTab(tab); // Removed as only DC tab is active
    };

    return (
        <div className="w-full">
            <div className="dashboard-header flex items-center justify-between px-4">
                <Hamburger />
                <div className="ms-5">
                    <TabSwitcher />
                </div>
                <div className="flex-1 flex justify-center">
                    <GridcoTitle />
                </div>
            </div>
            <div className="h-[calc(-73px+100vh)] p-4">
                <div className="w-full h-full flex flex-col gap-4">
                    <div className="gas-chart-container flex flex-col h-full overflow-hidden">
                        <div className="gas-heading">
                            <div className="flex w-full justify-between items-center gap-10">
                                <div className="flex gap-x-4 items-center">
                                    <div className="flex gap-x-1 items-center">
                                        <label className="text-md font-medium text-[#525062] whitespace-nowrap">Date:</label>
                                        <input
                                            type="date"
                                            value={selectedDate}
                                            onChange={(e) => setSelectedDate(e.target.value)}
                                            className="border border-[#C5C5CBD9] rounded-lg px-2 py-1.5 text-sm text-[#525062] focus:outline-none focus:border-[#34656D]"
                                        />
                                    </div>
                                    <UploadTabSwitcher
                                        activeTab={activeUploadTab}
                                        dcCount={dcUploadedCount}
                                        dcTotal={DC_FILE_TYPES.length}
                                    />
                                    <CustomSelect
                                        options={currentFileTypes}
                                        value={currentSelectedFileType}
                                        onChange={(val) => setCurrentSelectedFileType(val)}
                                    />
                                </div>
                                <div className="flex gap-x-4">
                                    <button
                                        type="button"
                                        onClick={handleGenerateReportDC}
                                        disabled={!isDateSelected || !allDcFilesUploaded || loading}
                                        className="flex items-center gap-2 px-6 py-2 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium hover:bg-[#d98453] transition-colors"
                                    >
                                        {loading ? "Processing..." : "Generate Report"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="mx-4 mt-4">
                            <div
                                className="p-4"
                                style={{
                                    backgroundColor: "#F1F5F980",
                                    border: "1px solid #CAD5E2",
                                    borderRadius: "15px",
                                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
                                }}
                            >
                                <div className="flex justify-between items-center">
                                    <div className="max-w-[70%]">
                                        <h3
                                            className="text-xl font-medium mb-1"
                                            style={{ color: "#6F6D7C" }}
                                        >
                                            Add DC Files
                                        </h3>
                                        <p className="text-sm" style={{ color: "#8C8A96" }}>
                                            Attach all the mentioned files below to run the DAM
                                            Performance Analysis. Upload DC (Declared Capacity) files.
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleBulkUploadClick}
                                        disabled={!isDateSelected}
                                        className="flex items-center justify-center gap-2 w-[180px] h-[37px] text-base rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
                                    >
                                        <CloudUpload size={20} /> Bulk Upload
                                    </button>
                                </div>
                                <input
                                    type="file"
                                    ref={scheduleBulkRef}
                                    onChange={handleBulkUpload}
                                    className="hidden"
                                    multiple
                                    accept=".xlsx, .xls, .csv, .pdf"
                                    key="schedule-bulk"
                                />
                                <input
                                    type="file"
                                    ref={dcBulkRef}
                                    onChange={handleBulkUpload}
                                    className="hidden"
                                    multiple
                                    accept=".xlsx, .xls, .csv, .pdf"
                                    key="dc-bulk"
                                />
                            </div>
                            <h2
                                className="py-2 mt-4 chartHeading border-b !text-lg !font-medium"
                                style={{ color: "#6F6D7C" }}
                            >
                                {activeUploadTab} File Types
                            </h2>

                            <div className={`overflow-y-auto relative py-2 mb-20 ${!isDateSelected ? 'pointer-events-none' : ''}`} style={{ height: "calc(100vh - 340px)" }}>
                                {!isDateSelected && (
                                    <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-lg">
                                        <p className="text-lg font-medium text-[#8C8A96]">Please select a date first</p>
                                    </div>
                                )}
                                {filesToDisplay.map((fileName: string) => {
                                    const uploadedFile =
                                        currentFiles.find((f: UploadedFile) => f.type === fileName)?.file || null;
                                    return (
                                        <FileRow
                                            key={`${activeUploadTab}-${fileName}`}
                                            fileName={fileName}
                                            uploadedFile={uploadedFile}
                                            fileError={currentFileErrors[fileName] || null}
                                            onFileChange={handleFileChange}
                                            onFileError={handleFileError}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                <ErrorPopup error={error} onClose={() => setError(null)} />
                <div className="gna-footer-container">
                    <div className="grid grid-cols-2 justify-between gap-5 ">
                        <p className="gna-footer-text col-span-1">
                            Powered by <strong>GNA Energy</strong>
                        </p>
                        <strong className="gna-footer-text col-span-1 text-end">
                            Beta Version
                        </strong>
                    </div>
                </div>
            </div>
        </div>
    );
}
